import { Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { button, element } from "@groundspark/browser-ui";

export class EmailEditor {
  public readonly element: HTMLElement;
  private readonly editor: Editor;

  public constructor(html: string, onChange: (html: string) => void) {
    const content = element("div");
    const toolbar = element("div", "editor-toolbar");
    toolbar.setAttribute("role", "group");
    toolbar.setAttribute("aria-label", "Email formatting");
    this.element = element("div", "email-editor", toolbar, content);
    this.editor = new Editor({
      element: content,
      injectCSS: false,
      extensions: [
        StarterKit.configure({
          code: false,
          codeBlock: false,
          horizontalRule: false,
          heading: { levels: [2, 3] },
          link: { openOnClick: false },
        }),
      ],
      content: html,
      editorProps: {
        attributes: {
          class: "email-editor__content",
          role: "textbox",
          "aria-label": "Email message",
          "aria-multiline": "true",
        },
      },
      onUpdate: ({ editor }) => onChange(editor.getHTML()),
    });
    const controls = [
      {
        label: "Bold",
        mark: "bold",
        run: () => this.editor.chain().focus().toggleBold().run(),
      },
      {
        label: "Italic",
        mark: "italic",
        run: () => this.editor.chain().focus().toggleItalic().run(),
      },
      {
        label: "Underline",
        mark: "underline",
        run: () => this.editor.chain().focus().toggleUnderline().run(),
      },
      {
        label: "Bullet list",
        mark: "bulletList",
        run: () => this.editor.chain().focus().toggleBulletList().run(),
      },
      {
        label: "Numbered list",
        mark: "orderedList",
        run: () => this.editor.chain().focus().toggleOrderedList().run(),
      },
    ].map(({ label, mark, run }) => {
      const control = button(label, "button button--white", () => {
        run();
      });
      toolbar.append(control);
      return { control, mark };
    });
    toolbar.append(
      button("Undo", "button button--white", () => {
        this.editor.chain().focus().undo().run();
      }),
      button("Redo", "button button--white", () => {
        this.editor.chain().focus().redo().run();
      }),
    );
    const updateToolbar = (): void => {
      for (const { control, mark } of controls)
        control.setAttribute("aria-pressed", String(this.editor.isActive(mark)));
    };
    this.editor.on("transaction", updateToolbar);
    updateToolbar();
  }

  public destroy(): void {
    this.editor.destroy();
  }
}
